import React, { useCallback, useContext, useMemo, useEffect } from 'react';
import { StyledReactSelect } from '../../common/StyledReactSelect.styled';
import { Box, BoxProps } from '@tapresearch/ui/components';
import { filterContext } from '../FilterProvider';

export interface Option {
  label: string;
  value: string;
}

export interface FilterMultipleSelectorProps
  extends Omit<BoxProps, 'onChange'> {
  name: string;
  options: Option[];
  value?: string[];
  onChange?: (name: string, value: string[]) => void;
  isDisabled?: boolean;
}

export function FilterMultipleSelector({
  options,
  onChange = () => {
    //
  },
  name,
  isDisabled = false,
  ...rest
}: FilterMultipleSelectorProps) {
  const context = useContext(filterContext);

  const defaultValues = (context.getDefaultValueByName(name) as string[]) || [];
  const values = context.filterValues[name] as string[];

  const optionsByValue = useMemo(
    () =>
      options.reduce<Record<string, Option>>((acc, option) => {
        acc[option.value] = option;
        return acc;
      }, {}),
    [options]
  );

  const selectedOptions = useMemo(
    () => {
      if (values) {
        return values.map((optionValue) => optionsByValue[optionValue]);
      }
      return [];
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [optionsByValue, defaultValues.join(','), values]
  );

  const defaultSelectedOptions = useMemo(
    () => {
      return (defaultValues || []).map(
        (optionValue) => optionsByValue[optionValue]
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [optionsByValue, defaultValues.join(',')]
  );

  // Update the labels from the default values on the context so they can be shown elsewhere
  useEffect(() => {
    const labels = selectedOptions.map((option) => option.label);
    context.onFilterItemLabelChange(name, labels);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValues.join(',')]);

  const innerOnChange = useCallback(
    (options: Option[]) => {
      const values = options.map((option) => option.value);
      const labels = options.map((option) => option.label);
      onChange(name, values);
      context.onFilterItemChange(name, values, labels);
    },

    [onChange, name, context]
  );

  return (
    <Box
      {...rest}
      margin="0"
      data-testid={
        ((rest as any)['data-testid'] as string) ||
        `${name ? name + '-' : ''}filter-multiple-select-wrapper`
      }
    >
      <StyledReactSelect
        isMulti
        getOptionLabel={(option: Option) => option.label}
        getOptionValue={(option: Option) => option.value}
        name="qualification_answers"
        defaultValue={defaultSelectedOptions}
        closeMenuOnSelect={false}
        options={options}
        value={selectedOptions}
        onChange={innerOnChange}
        className="basic-multi-select"
        classNamePrefix="select"
        placeholder="Click to see options"
        isClearable={true}
        isDisabled={isDisabled}
      />
    </Box>
  );
}
